import { useEffect, useState } from 'react';
import {
  Grid, Card, CardContent, Typography, Chip, Paper, Table, TableHead, TableRow,
  TableCell, TableBody, Alert, Box,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import InventoryIcon from '@mui/icons-material/Inventory';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FolderIcon from '@mui/icons-material/Folder';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const STATUS_COLOR = { PENDING_REVIEW: 'warning', APPROVED: 'success', SUSPENDED: 'error', BLACKLISTED: 'error' };
// Compliance traffic light per the wireframe (green / yellow / red).
const STATUS_LIGHT = { APPROVED: '#2E7D32', PENDING_REVIEW: '#ED6C02', SUSPENDED: '#C62828', BLACKLISTED: '#C62828' };

function Widget({ label, value, onClick, accent }) {
  return (
    <Card sx={{ cursor: onClick ? 'pointer' : 'default', height: '100%' }} onClick={onClick}>
      <CardContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>{label}</Typography>
        {accent
          ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: accent }} />
              <Typography variant="h6">{value}</Typography>
            </Box>
          : <Typography variant="h4" color="primary">{value}</Typography>}
      </CardContent>
    </Card>
  );
}

export default function VendorDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [openTenders, setOpenTenders] = useState([]);

  useEffect(() => {
    (async () => {
      const [p, t] = await Promise.all([
        api.get('/vendors/me/profile'),
        api.get('/public/tenders/open'),
      ]);
      setProfile(p.data.data);
      setOpenTenders(t.data.data);
    })();
  }, []);

  // Left navigation exactly per wireframe (Phase 2+ items disabled).
  const nav = [
    { label: 'Dashboard', path: '/vendor', icon: <DashboardIcon /> },
    { label: 'Open Tenders', path: '/vendor/tenders', icon: <DescriptionIcon /> },
    { label: 'My Bids', path: '/vendor/bids', icon: <InventoryIcon /> },
    { label: 'Clarifications', icon: <QuestionAnswerIcon />, disabled: true },
    { label: 'Award Results', icon: <EmojiEventsIcon />, disabled: true },
    { label: 'Profile & Documents', path: '/vendor/profile', icon: <FolderIcon /> },
  ];

  const complianceLabel = profile ? profile.status.replace('_', ' ') : '—';

  return (
    <DashboardLayout nav={nav} welcomeName={profile?.companyName || user?.fullName} title="Dashboard">
      {profile && profile.status !== 'APPROVED' && (
        <Alert severity={profile.status === 'PENDING_REVIEW' ? 'info' : 'error'} sx={{ mb: 3 }}>
          {profile.status === 'PENDING_REVIEW'
            ? 'Your account is under review. You can browse tenders now, and will be able to submit bids once approved.'
            : `Your account is ${profile.status.toLowerCase()}. Contact the procurement office for assistance.`}
        </Alert>
      )}

      {/* Four widgets exactly per wireframe */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Widget label="Open Tenders" value={openTenders.length} onClick={() => navigate('/vendor/tenders')} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Widget label="Submitted Bids" value={0} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Widget label="Pending Clarifications" value={0} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Widget label="Compliance Status" value={complianceLabel} accent={profile ? STATUS_LIGHT[profile.status] : '#999'} />
        </Grid>
      </Grid>

      {/* Recent activity log per wireframe bottom section */}
      <Typography variant="h6" sx={{ mb: 2 }}>Recent activity</Typography>
      <Paper sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow><TableCell>Activity</TableCell><TableCell>Detail</TableCell><TableCell>Date</TableCell></TableRow>
          </TableHead>
          <TableBody>
            {profile && (
              <TableRow>
                <TableCell>Registration submitted</TableCell>
                <TableCell><Chip size="small" label={complianceLabel} color={STATUS_COLOR[profile.status]} /></TableCell>
                <TableCell>{profile.createdAt ? new Date(profile.createdAt).toLocaleDateString('en-GB') : '—'}</TableCell>
              </TableRow>
            )}
            {!profile && (
              <TableRow><TableCell colSpan={3}><Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No recent activity.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </DashboardLayout>
  );
}
